import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaLinkedin, FaTwitter, FaFacebook, FaWhatsapp, FaInstagram, FaEnvelope, FaPhone, FaMapMarkerAlt, FaArrowRight } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import mainLogo from '../assets/codewave-logo.png';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState(null);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email && /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      // Here you would normally send this to your backend
      setSubscribeStatus('success');
      setEmail('');
      setTimeout(() => setSubscribeStatus(null), 3000);
    } else {
      setSubscribeStatus('error');
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <FooterContainer>
      <TopFooter>
        <Container>
          <FooterGrid>
            <CompanySection>
              <LogoImage src={mainLogo} alt="Codewave Technologies" onClick={() => window.location.href = '/'} />
              <CompanyDescription>
                Empowering businesses with cutting-edge communication solutions across Africa.
                We provide SMS, USSD, and custom software development services.
              </CompanyDescription>
              <SocialLinks>
                <SocialLink href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                  <FaLinkedin />
                </SocialLink>
                <SocialLink href="https://twitter.com" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                  <FaTwitter />
                </SocialLink>
                <SocialLink href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                  <FaFacebook />
                </SocialLink>
                <SocialLink href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                  <FaInstagram />
                </SocialLink>
                {/* Added WhatsApp Link */}
                <SocialLink
                  href="https://wa.me/254757504508"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Chat on WhatsApp"
                  className="whatsapp-link"
                >
                  <FaWhatsapp />
                </SocialLink>
              </SocialLinks>
            </CompanySection>

            <QuickLinksSection>
              <FooterHeading>Quick Links</FooterHeading>
              <LinkList>
                <LinkItem><FooterLink to="/">Home</FooterLink></LinkItem>
                <LinkItem><FooterLink to="/about">About Us</FooterLink></LinkItem>
                <LinkItem><FooterLink to="/services">Our Services</FooterLink></LinkItem>
                <LinkItem><FooterLink to="/blog">Blog</FooterLink></LinkItem>
                <LinkItem><FooterLink to="/contact">Contact Us</FooterLink></LinkItem>
              </LinkList>
            </QuickLinksSection>

            <ServicesSection>
              <FooterHeading>Our Services</FooterHeading>
              <LinkList>
                <LinkItem><FooterLink to="/services/bulk-sms">Bulk SMS</FooterLink></LinkItem>
                <LinkItem><FooterLink to="/services/ussd">USSD Services</FooterLink></LinkItem>
                <LinkItem><FooterLink to="/services/call-center">Call Center Solutions</FooterLink></LinkItem>
                <LinkItem><FooterLink to="/services/payment">Payment Solutions</FooterLink></LinkItem>
                <LinkItem><FooterLink to="/services/software">Software Development</FooterLink></LinkItem>
              </LinkList>
            </ServicesSection>

            <ContactSection>
              <FooterHeading>Contact Info</FooterHeading>
              <ContactList>
                <ContactItem>
                  <ContactIcon><FaPhone /></ContactIcon>
                  <ContactText>+254 757 504508</ContactText>
                  <WhatsAppContact
                    href="https://wa.me/254757504508"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FaWhatsapp /> Chat on WhatsApp
                  </WhatsAppContact>
                </ContactItem>
                <ContactItem>
                  <ContactIcon><FaEnvelope /></ContactIcon>
                  <ContactText>info@codewave.co.ke</ContactText>
                </ContactItem>
                <ContactItem>
                  <ContactIcon><FaMapMarkerAlt /></ContactIcon>
                  <ContactText>Red Commercial Complex, Mombasa Road, Nairobi, Kenya</ContactText>
                </ContactItem>
              </ContactList>
            </ContactSection>

            <NewsletterSection>
              <FooterHeading>Newsletter</FooterHeading>
              <NewsletterText>
                Subscribe to get the latest news and updates from Codewave.
              </NewsletterText>
              <NewsletterForm onSubmit={handleSubscribe}>
                <NewsletterInput
                  type="email"
                  placeholder="Your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  hasError={subscribeStatus === 'error'}
                />
                <SubscribeButton type="submit">
                  <FaArrowRight />
                </SubscribeButton>
              </NewsletterForm>
              {subscribeStatus === 'success' && (
                <SuccessMessage>Thank you for subscribing!</SuccessMessage>
              )}
              {subscribeStatus === 'error' && (
                <ErrorMessage>Please enter a valid email</ErrorMessage>
              )}
            </NewsletterSection>
          </FooterGrid>
        </Container>
      </TopFooter>

      <BottomFooter>
        <Container>
          <BottomFooterContent>
            <Copyright>
              © {currentYear} Codewave. All Rights Reserved.
            </Copyright>
            <FooterLinks>
              <FooterLink to="/privacy-policy">Privacy Policy</FooterLink>
              <FooterLink to="/terms-of-service">Terms of Service</FooterLink>
            </FooterLinks>
          </BottomFooterContent>
        </Container>
      </BottomFooter>
    </FooterContainer>
  );
};

export default Footer;

// === Animations ===
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const gradientAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

// === Styled Components ===
const FooterContainer = styled.footer`
  width: 100%;
  background-color: #1a2540;
  color: white;
`;

const TopFooter = styled.div`
  padding: 70px 40px;
  
  @media (max-width: 992px) {
    padding: 50px 30px;
  }
  
  @media (max-width: 576px) {
    padding: 40px 20px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 30px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(6, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 40px 20px;
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const CompanySection = styled.div`
  grid-column: span 4;
  animation: ${fadeIn} 0.4s ease-out;
  
  @media (max-width: 992px) {
    grid-column: span 6;
  }
  
  @media (max-width: 768px) {
    grid-column: span 2;
  }
  
  @media (max-width: 576px) {
    grid-column: span 1;
  }
`;

const LogoImage = styled.img`
  height: 50px;
  width: auto;
  margin-bottom: 20px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
  
  @media (max-width: 768px) {
    height: 40px;
  }
`;

const CompanyDescription = styled.p`
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  margin-bottom: 20px;
  font-size: 15px;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 15px;
  
  /* Specific styling for WhatsApp icon */
  .whatsapp-link {
    background-color: #25D366;
    
    &:hover {
      background-color: #20c35f;
    }
  }
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 16px;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #2090B0;
    transform: translateY(-3px);
  }
`;

const QuickLinksSection = styled.div`
  grid-column: span 2;
  animation: ${fadeIn} 0.4s ease-out 0.1s forwards;
  opacity: 0;
  
  @media (max-width: 992px) {
    grid-column: span 3;
  }
  
  @media (max-width: 768px) {
    grid-column: span 1;
  }
`;

const ServicesSection = styled.div`
  grid-column: span 2;
  animation: ${fadeIn} 0.4s ease-out 0.2s forwards;
  opacity: 0;
  
  @media (max-width: 992px) {
    grid-column: span 3;
  }
  
  @media (max-width: 768px) {
    grid-column: span 1;
  }
`;

const ContactSection = styled.div`
  grid-column: span 2;
  animation: ${fadeIn} 0.4s ease-out 0.3s forwards;
  opacity: 0;
  
  @media (max-width: 992px) {
    grid-column: span 3;
  }
  
  @media (max-width: 768px) {
    grid-column: span 1;
  }
`;

const NewsletterSection = styled.div`
  grid-column: span 2;
  animation: ${fadeIn} 0.4s ease-out 0.4s forwards;
  opacity: 0;
  
  @media (max-width: 992px) {
    grid-column: span 3;
  }
  
  @media (max-width: 768px) {
    grid-column: span 1;
  }
`;

const FooterHeading = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 20px;
  position: relative;
  color: white;
  padding-bottom: 10px;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 30px;
    height: 2px;
    background-color: #2090B0;
  }
`;

const LinkList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const LinkItem = styled.li`
  margin-bottom: 10px;
`;

const FooterLink = styled(Link)`
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 15px;
  position: relative;
  
  &:hover {
    color: #2090B0;
    padding-left: 5px;
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 1px;
    background-color: #2090B0;
    transition: width 0.3s ease;
  }
  
  &:hover:after {
    width: 100%;
  }
`;

const ContactList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const ContactItem = styled.li`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  flex-wrap: wrap;
  margin-bottom: 15px;
  color: rgba(255, 255, 255, 0.7);
  
  &:first-child {
    margin-bottom: 20px;
  }
`;

const ContactIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.05);
  color: #2090B0;
  margin-right: 12px;
  font-size: 14px;
  flex-shrink: 0;
`;

const ContactText = styled.span`
  font-size: 15px;
  line-height: 30px; /* Match icon height for vertical centering */
  flex: 1;
  min-width: 0;
`;

/* New styled component for WhatsApp contact link */
const WhatsAppContact = styled.a`
  display: inline-flex;
  align-items: center;
  margin-top: 8px;
  padding: 5px 12px;
  background-color: #25D366;
  color: white;
  border-radius: 20px;
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
  width: fit-content;
  margin-left: 42px; /* 30px icon + 12px margin-right = align with text */
  flex-basis: 100%; /* Force onto new line */
  transition: all 0.3s ease;
  
  svg {
    margin-right: 5px;
  }
  
  &:hover {
    background-color: #20c35f;
    transform: translateY(-2px);
  }
`;

const NewsletterText = styled.p`
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  margin-bottom: 20px;
  font-size: 15px;
`;

const NewsletterForm = styled.form`
  display: flex;
  margin-bottom: 10px;
`;

const NewsletterInput = styled.input`
  flex: 1;
  padding: 12px 15px;
  border: 1px solid ${props => props.hasError ? '#ff4d4f' : 'rgba(255, 255, 255, 0.1)'};
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 4px 0 0 4px;
  font-size: 14px;
  color: white;
  outline: none;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
  
  &:focus {
    border-color: ${props => props.hasError ? '#ff4d4f' : '#2090B0'};
  }
`;

const SubscribeButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 15px;
  background-color: #2090B0;
  border: none;
  border-radius: 0 4px 4px 0;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #a52864;
  }
`;

const SuccessMessage = styled.p`
  color: #52c41a;
  font-size: 13px;
  margin: 5px 0;
`;

const ErrorMessage = styled.p`
  color: #ff4d4f;
  font-size: 13px;
  margin: 5px 0;
`;

const BottomFooter = styled.div`
  padding: 20px 40px;
  background-color: #14192b;
  
  @media (max-width: 576px) {
    padding: 20px;
  }
`;

const BottomFooterContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  @media (max-width: 576px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const Copyright = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  margin: 0;
`;

const FooterLinks = styled.div`
  display: flex;
  gap: 20px;
  
  a {
    color: rgba(255, 255, 255, 0.6);
    font-size: 14px;
    
    &:hover {
      padding-left: 0;
    }
  }
`;
